import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface AdminPasswordConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (password: string) => Promise<void>;
  title?: string;
  description?: string;
  actionLabel?: string;
}

export const AdminPasswordConfirmDialog: React.FC<AdminPasswordConfirmDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  title = "Confirm Administrative Action",
  description = "This action requires admin password confirmation for security. Please enter your password to continue.",
  actionLabel = "Confirm"
}) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Clear password when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setPassword('');
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!password) {
      toast({
        title: "Password Required",
        description: "Please enter your password to confirm this action.",
        variant: "destructive",
      });
      return;
    }

    // Defensive check: password shouldn't contain error messages or stack traces
    if (password.includes('Error') || password.includes('globalErrorHandler') || password.includes('at async')) {
      toast({
        title: "Invalid Password",
        description: "The password field contains invalid data. Please clear the field and re-enter your password.",
        variant: "destructive",
      });
      setPassword('');
      return;
    }

    console.log('Password field length:', password.length); // Debug log

    setIsLoading(true);
    try {
      await onConfirm(password);
      setPassword('');
      onOpenChange(false);
    } catch (error: any) {
      // Error handling is done by the parent component
      console.error('Password confirmation error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setPassword('');
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="admin-password">Administrator Password</Label>
            <Input
              id="admin-password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              autoFocus
              autoComplete="current-password"
              data-lpignore="true"
              data-form-type="other"
            />
          </div>

          <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 p-3 text-sm text-yellow-800 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800">
            <p className="font-medium mb-1">Security Notice</p>
            <p className="text-xs">
              This action will modify critical system pricing settings.
              Your password is required to prevent unauthorized changes.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || !password}
          >
            {isLoading ? 'Verifying...' : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
